"use client";

import { motion, useMotionValue, useSpring, useTransform, AnimatePresence } from "framer-motion";
import { MouseEvent, useRef, useState, useEffect } from "react";
import Magnetic from "./ui/Magnetic";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import { useGSAP } from "@gsap/react";

gsap.registerPlugin(ScrollTrigger);

interface Project {
  title: string;
  subtitle: string;
  status: string;
  statusColor: string;
  year: string;
  description: string;
  badges: { name: string; color: string }[];
  liveUrl: string;
  githubUrl: string;
  accentColor: string;
  icon: string;
  
  // Extended Engineering Details for Modals
  problem: string;
  research: string;
  architecture: string;
  performance: string;
  authentication: string;
  databaseSchema: string;
  apiStructure: string[];
  deployment: string;
  codeSnippet: string;
  roadmap: string[];
}

const projects: Project[] = [
  {
    title: "Clickotion",
    subtitle: "Agency Client Portal & PM",
    status: "Active",
    statusColor: "bg-indigo-500/20 text-indigo-300 border border-indigo-500/20",
    year: "2026",
    description: "A premium client portal and project management platform built for a marketing & software development agency. Features real-time Chatwoot CRM widget integration, custom project dashboards (milestones, tasks, comments), and automated Cashfree payment gateway webhook processing.",
    badges: [
      { name: "Next.js 15", color: "bg-indigo-500/15 text-indigo-300 border-indigo-500/20" },
      { name: "PostgreSQL", color: "bg-indigo-500/15 text-indigo-300 border-indigo-500/20" },
      { name: "Cashfree PG", color: "bg-indigo-500/15 text-indigo-300 border-indigo-500/20" },
      { name: "Chatwoot API", color: "bg-indigo-500/15 text-indigo-300 border-indigo-500/20" },
    ],
    liveUrl: "https://clickotion.com",
    githubUrl: "https://github.com/CodeByAmrit",
    accentColor: "from-indigo-500/10 to-purple-500/5",
    icon: "https://raw.githubusercontent.com/CodeByAmrit/File-Manager/refs/heads/main/icons/1.png",
    problem: "Agency client coordination, milestone invoice verification, and task tracking are traditionally manual, slow, and prone to communication gaps across emails, bank receipts, and support channels.",
    research: "Designed real-time iframe postMessage protocols to link active user profiles within Chatwoot sidebars. Optimized Lighthouse audits (PageSpeed) to score 100/100 in contrast ratios and screen-reader accessibility.",
    architecture: "Monolithic Next.js client-facing web app using PostgreSQL. The payment gateway triggers one-time checkout links via REST APIs, while a webhook listener intercepts payment events, verifies signatures, and updates database records.",
    performance: "Sitemap render speed: <50ms. PageSpeed Lighthouse audit score: 98+ Accessibility & SEO performance rating.",
    authentication: "NextAuth.js session-based OAuth authentication paired with cryptographically verified webhook event signatures.",
    databaseSchema: "CREATE TABLE projects (id SERIAL PRIMARY KEY, title VARCHAR(255), client_id INT REFERENCES users(id)); CREATE TABLE milestones (id SERIAL PRIMARY KEY, project_id INT, amount DECIMAL, status VARCHAR(50)); CREATE TABLE tasks (id SERIAL PRIMARY KEY, title VARCHAR(255), status VARCHAR(50));",
    apiStructure: [
      "POST /api/payments/cashfree/create - Initializes a dynamic payment link session for a milestone",
      "POST /api/webhooks/cashfree - Receives and verifies payment gateway notifications to auto-release milestones",
      "GET /api/projects - Queries active campaigns, checklists, and chat logs for the client dashboard"
    ],
    deployment: "Dockerized containerized deployment orchestrated through Dokploy on a virtual private server (VPS).",
    codeSnippet: `// Generates dynamic Cashfree payment order link for client milestones
async function createMilestonePaymentLink(orderId, amount, customerEmail) {
  const response = await fetch("https://api.cashfree.com/pg/orders", {
    method: "POST",
    headers: {
      "x-client-id": process.env.CASHFREE_APP_ID,
      "x-client-secret": process.env.CASHFREE_SECRET_KEY,
      "x-api-version": "2023-08-01",
      "Content-Type": "application/json"
    },
    body: JSON.stringify({
      order_id: orderId,
      order_amount: amount,
      order_currency: "INR",
      customer_details: { customer_id: customerEmail, customer_email: customerEmail }
    })
  });
  return response.json();
}`,
    roadmap: [
      "Integrate end-to-end encrypted file lockers for secure client deliverable uploads",
      "Add multi-regional localization options for automated invoice tax compilation"
    ]
  },
  {
    title: "StreamVision",
    subtitle: "Govt Infrastructure",
    status: "Live",
    statusColor: "bg-green-500/20 text-green-400 border border-green-500/20",
    year: "2025",
    description: "Architected a high-concurrency CCTV monitoring system for the Haryana Government. Features low-latency RTSP-to-HLS transcoding and real-time motion forensics.",
    badges: [
      { name: "FFmpeg", color: "bg-purple-500/15 text-purple-300 border-purple-500/20" },
      { name: "Node.js", color: "bg-purple-500/15 text-purple-300 border-purple-500/20" },
      { name: "Docker", color: "bg-purple-500/15 text-purple-300 border-purple-500/20" },
    ],
    liveUrl: "https://cctvweblink.in",
    githubUrl: "https://github.com/CodeByAmrit/streamVision",
    accentColor: "from-purple-500/10 to-pink-500/5",
    icon: "https://github.com/CodeByAmrit/StreamVision/blob/master/public/images/favicon.png?raw=true",
    problem: "Legacy analog cameras stream RTSP feeds incompatible with modern HTML5 video elements, creating security access latency bottlenecks for remote field audits.",
    research: "Evaluated HLS fragment segment durations. Discovered that 2-second segments minimize stream buffer lag without causing client network segment overload.",
    architecture: "Ingress RTSP feed → FFmpeg Alpine Transcoder (Segmented HLS) → Local Storage node → NGINX Static segment publisher with client caching headers.",
    performance: "Live stream lag delay: Reduced from 12s to 1.8s. Concurrent active viewers limit: 500+ streams.",
    authentication: "Hardware UUID address verified via dynamic token handshakes before authorizing stream access.",
    databaseSchema: "TABLE Feeds (id INT AUTO_INCREMENT PRIMARY KEY, name VARCHAR(50), rtsp_url TEXT, active BOOLEAN, logs_count INT);",
    apiStructure: [
      "GET /api/feeds - Returns list of configured active camera streams",
      "POST /api/feeds/transcode/:id - Triggers transcoding subprocess start"
    ],
    deployment: "Deployed inside isolated Docker containers across physical local edge servers, exposed safely via Cloudflare Tunnel.",
    codeSnippet: `// Spawns Alpine FFmpeg instance to segment camera stream
const transcode = spawn('ffmpeg', [
  '-i', rtspUrl,
  '-c:v', 'libx264',
  '-hls_time', '2',
  '-hls_list_size', '5',
  '-hls_flags', 'delete_segments',
  'stream.m3u8'
]);`,
    roadmap: [
      "Implement edge TensorFlow models for automated vehicle license plate logging",
      "Support direct recording uploads to AWS S3 Glacier repositories"
    ]
  },
  {
    title: "Student Tracker",
    subtitle: "Enterprise SMS",
    status: "Live",
    statusColor: "bg-blue-500/20 text-blue-400 border border-blue-500/20",
    year: "2026",
    description: "A sophisticated educational ERP featuring multi-tenant architecture, automated reporting, and secure RBAC. Designed for seamless academic administration.",
    badges: [
      { name: "Vue.js 3", color: "bg-blue-500/15 text-blue-300 border-blue-500/20" },
      { name: "Express", color: "bg-blue-500/15 text-blue-300 border-blue-500/20" },
      { name: "Redis", color: "bg-blue-500/15 text-blue-300 border-blue-500/20" },
    ],
    liveUrl: "https://edu.amritsharma.dev",
    githubUrl: "https://github.com/codebyamrit/school",
    accentColor: "from-blue-500/10 to-cyan-500/5",
    icon: "https://raw.githubusercontent.com/CodeByAmrit/StudentTracker-Pro/refs/heads/master/public/favicon.png",
    problem: "Standard educational platforms fail to scale sub-accounts permissions properly, leaving student data vulnerable or overloading servers during grading periods.",
    research: "Engineered strict database connection pooling and decoupled heavy PDF generation routines via async background threads.",
    architecture: "Vue 3 Client Ingress → NGINX → Express Cluster API → Redis Session Cache → MySQL Relational DB.",
    performance: "Dashboard load speed: 320ms. PDF Grade Sheet print speed: 1.5s per batch of 100 students.",
    authentication: "Express Cookie Session middleware synced with Redis cache to verify roles permissions mapping.",
    databaseSchema: "TABLE Students (id INT PRIMARY KEY, name VARCHAR(100), class VARCHAR(10), parent_contact VARCHAR(15), grades JSON);",
    apiStructure: [
      "POST /api/students/grade - Updates student test grades",
      "GET /api/reports/pdf - Generates report card PDF stream"
    ],
    deployment: "Deployed using Docker Compose containers on an Infosys internal virtual private cloud node with backup database replication.",
    codeSnippet: `// Decoupled PDF generation using queues and child processes
app.post("/api/reports/pdf", async (req, res) => {
  const job = await reportQueue.add({ studentId: req.body.id });
  res.status(202).json({ jobId: job.id, status: "processing" });
});`,
    roadmap: [
      "Implement automatic WhatsApp message API triggers for daily attendance logging",
      "Add interactive canvas charts for grade distributions analysis"
    ]
  },
  {
    title: "Portfolio Hub",
    subtitle: "This Portfolio Product",
    status: "Active",
    statusColor: "bg-purple-500/20 text-purple-400 border border-purple-500/20",
    year: "2026",
    description: "Highly optimized static portfolio product built with Next.js 16 and Tailwind 4. Incorporates smooth scroll dynamics, mouse glow grids, and keyboard terminal CLI.",
    badges: [
      { name: "Next.js 16", color: "bg-purple-500/15 text-purple-300 border-purple-500/20" },
      { name: "Framer Motion", color: "bg-purple-500/15 text-purple-300 border-purple-500/20" },
      { name: "Tailwind 4", color: "bg-purple-500/15 text-purple-300 border-purple-500/20" },
    ],
    liveUrl: "https://amritsharma.dev",
    githubUrl: "https://github.com/CodeByAmrit/codebyamrit.github.io",
    accentColor: "from-purple-500/10 to-pink-500/5",
    icon: "https://github.com/logo.svg",
    problem: "Most portfolio templates look generic, lack interactive system design details, load slowly, and fail to validate the technical depth of senior developers.",
    research: "Optimized Core Web Vitals targets. Leveraged static exports to ensure immediate page delivery times under global CDN loads.",
    architecture: "Next.js static client export → Served globally via GitHub Pages CDN nodes, optimized with edge cache rules.",
    performance: "Lighthouse Score: 100/100 Performance, 100/100 SEO. Time To First Byte (TTFB): 18ms.",
    authentication: "None (Client public profile display node). Secure headers applied via routing rules.",
    databaseSchema: "No database (Static state assets structure). Mock statistics populated via structured JSON configurations.",
    apiStructure: [
      "GET / - Delivers HTML index bundle",
      "GET /manifest.json - Delivers PWA metadata mappings"
    ],
    deployment: "Automated deployment pipeline using GitHub Actions building the Next.js static bundle and pushing directly to gh-pages branch.",
    codeSnippet: `// next.config.ts configuration mapping
const nextConfig = {
  output: "export",
  trailingSlash: true,
  images: { unoptimized: true }
};
export default nextConfig;`,
    roadmap: [
      "Add interactive WebGL Three.js 3D nodes mapping your complete code workspace",
      "Integrate local search indexes for searching code snippets directly from the UI"
    ]
  }
];

function Card({ project, onClick }: { project: Project; onClick: () => void }) {
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  const mouseSpringConfig = { damping: 20, stiffness: 100 };
  const springX = useSpring(x, mouseSpringConfig);
  const springY = useSpring(y, mouseSpringConfig);

  const rotateX = useTransform(springY, [-100, 100], [10, -10]);
  const rotateY = useTransform(springX, [-100, 100], [-10, 10]);

  function handleMouseMove(event: MouseEvent) {
    const { currentTarget, clientX, clientY } = event;
    const { left, top, width, height } = currentTarget.getBoundingClientRect();
    const centerX = left + width / 2;
    const centerY = top + height / 2;
    x.set(clientX - centerX);
    y.set(clientY - centerY);
    mouseX.set(clientX - left);
    mouseY.set(clientY - top);
  }

  function handleMouseLeave() {
    x.set(0);
    y.set(0);
  }

  return (
    <motion.div
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onClick={onClick}
      style={{ rotateX, rotateY, perspective: 1000 }}
      className={`project-card shrink-0 w-[78vw] md:w-[360px] h-[340px] relative glass-card rounded-3xl overflow-hidden border border-white/10 bg-gradient-to-br ${project.accentColor} flex flex-col p-6 transition-colors duration-300 hover:border-indigo-500/40 cursor-pointer`}
    >
      <div className="flex items-start justify-between mb-4 relative z-10">
        <div className="flex items-center gap-3">
          <Magnetic>
            <div className="w-14 h-14 rounded-xl flex items-center justify-center p-2 shadow-xl bg-white/5 border border-white/10">
              <i className="fi fi-rr-folder text-2xl text-indigo-400" />
            </div>
          </Magnetic>
          <div>
            <h3 className="text-xl font-black text-white font-[var(--font-poppins)] tracking-tight mb-0.5">
              {project.title}
            </h3>
            <span className="text-[9px] font-bold text-gray-400 uppercase tracking-[0.2em]">{project.subtitle}</span>
          </div>
        </div>
        <span className={`text-[8px] px-2 py-0.5 rounded-full font-black uppercase tracking-widest ${project.statusColor}`}>
          {project.status}
        </span>
      </div>

      <p className="text-gray-400 text-xs leading-relaxed mb-4 font-medium relative z-10 line-clamp-3">
        {project.description}
      </p>

      <div className="flex flex-wrap gap-1.5 mb-6 relative z-10">
        {project.badges.map((badge) => (
          <span key={badge.name} className={`text-[8px] px-2 py-1 rounded-lg border font-black uppercase tracking-widest ${badge.color}`}>
            {badge.name}
          </span>
        ))}
      </div>

      <div className="flex items-center justify-between mt-auto pt-4 border-t border-white/5 relative z-10">
        <span className="text-xs font-black uppercase tracking-widest text-indigo-300">
          Inspect Specs →
        </span>
        <Magnetic>
          <a
            href={project.liveUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-500/10 border border-indigo-500/20 text-[9px] font-black uppercase tracking-widest text-indigo-300 hover:bg-indigo-500/20 hover:text-white transition-all cursor-pointer"
            title="Open Live Site in a new tab"
          >
            <span>Live Site</span>
            <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        </Magnetic>
      </div>
    </motion.div>
  );
}

export default function Projects() {
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const [progress, setProgress] = useState(0);
  const [activeProject, setActiveProject] = useState<Project | null>(null);

  useEffect(() => {
    if (activeProject) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
  }, [activeProject]);

  useGSAP(() => {
    if (!containerRef.current || !triggerRef.current) return;
    if (window.innerWidth < 768) return; // Disable GSAP scroll on mobile

    const sections = gsap.utils.toArray<Element>(".project-card");
    const amountToScroll = containerRef.current.scrollWidth - window.innerWidth;

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: triggerRef.current,
        start: "top top",
        end: `+=${containerRef.current.scrollWidth}`,
        pin: true,
        scrub: 1,
        onUpdate: (self) => setProgress(self.progress),
      },
    });

    tl.to(containerRef.current, {
      x: -amountToScroll,
      ease: "none",
    });

    sections.forEach((section) => {
      gsap.to(section, {
        opacity: 1,
        scale: 1.03,
        duration: 0.5,
        scrollTrigger: {
          trigger: section,
          containerAnimation: tl,
          start: "left center",
          end: "right center",
          toggleActions: "play reverse play reverse",
          scrub: true,
        }
      });
    });

    return () => {
      ScrollTrigger.getAll().forEach(t => t.kill());
    };
  }, { scope: triggerRef });

  return (
    <section id="projects" ref={triggerRef} className="relative z-10 overflow-hidden bg-black/50">
      {/* Ambient Glow */}
      <motion.div
        animate={{ opacity: progress > 0 && progress < 1 ? 1 : 0 }}
        className="absolute inset-0 pointer-events-none transition-opacity duration-1000"
      >
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80vw] h-[80vh] bg-indigo-500/10 blur-[150px] rounded-full" />
      </motion.div>

      <div className="min-h-screen flex flex-col justify-center py-10 md:py-16">
        {/* Header */}
        <div className="max-w-7xl mx-auto px-6 mb-8 md:mb-12 w-full">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            viewport={{ once: true }}
            className="text-left"
          >
            <span className="text-indigo-400 text-xs font-black uppercase tracking-[0.4em] mb-4 block">Selected Works</span>
            <h2 className="text-4xl md:text-5xl lg:text-7xl font-black text-white mb-6 font-[var(--font-poppins)] tracking-tighter">
              Digital <span className="gradient-text">Masterpieces</span>
            </h2>
          </motion.div>
        </div>

        {/* Cards Row */}
        <div className="overflow-x-auto md:overflow-visible pb-12 md:pb-0">
          <div
            ref={containerRef}
            className="flex flex-col md:flex-row gap-8 px-6 md:px-[10vw] w-max md:w-auto"
          >
            {projects.map((project) => (
              <Card key={project.title} project={project} onClick={() => setActiveProject(project)} />
            ))}

            {/* View More Card */}
            <div className="shrink-0 w-[70vw] md:w-[280px] h-[320px] flex items-center justify-center">
              <Magnetic>
                <a
                  href="https://github.com/CodeByAmrit"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex flex-col items-center gap-6 p-12 rounded-3xl border border-white/5 bg-white/[0.02] hover:bg-white/[0.05] transition-all duration-500"
                >
                  <div className="w-20 h-20 rounded-full bg-white/5 flex items-center justify-center text-3xl group-hover:scale-110 group-hover:bg-indigo-500/10 transition-all duration-500 text-gray-400 hover:text-white">
                    <i className="fi fi-rr-arrow-right" />
                  </div>
                  <span className="text-xs font-black uppercase tracking-[0.3em] text-gray-400 group-hover:text-white transition-colors">Full Archive</span>
                </a>
              </Magnetic>
            </div>
          </div>
        </div>

        {/* Progress Bar (Desktop Only) */}
        <div className="hidden md:block absolute bottom-12 left-1/2 -translate-x-1/2 w-[40vw] h-px bg-white/10 overflow-hidden">
          <motion.div
            className="h-full bg-indigo-500 shadow-[0_0_15px_rgba(99,102,241,0.5)]"
            style={{ width: `${progress * 100}%` }}
          />
        </div>

        {/* Mobile Swipe Hint */}
        <div className="md:hidden text-center mt-8 text-[10px] font-black uppercase tracking-[0.3em] text-gray-500 animate-pulse">
          Swipe & Tap to inspect architecture
        </div>
      </div>

      {/* Slide-out High Fidelity Dialog Overlay */}
      <AnimatePresence>
        {activeProject && (
          <div className="fixed inset-0 z-[80] flex items-stretch justify-end">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setActiveProject(null)}
              className="absolute inset-0 bg-black/85 backdrop-blur-md"
            />

            {/* Modal Drawer content */}
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 200 }}
              data-lenis-prevent
              className="relative w-full max-w-3xl bg-[#090616]/95 border-l border-white/10 shadow-2xl p-6 md:p-10 overflow-y-auto z-10 custom-scrollbar flex flex-col"
            >
              <div className="absolute top-0 left-0 w-64 h-64 bg-indigo-500/5 rounded-full blur-[100px] pointer-events-none" />

              {/* Close and Links Header */}
              <div className="flex items-center justify-between mb-8 relative z-10 border-b border-white/5 pb-4">
                <button
                  onClick={() => setActiveProject(null)}
                  className="flex items-center gap-2 text-xs font-black uppercase tracking-widest text-indigo-400 hover:text-white transition-colors cursor-pointer"
                >
                  <i className="fi fi-rr-arrow-left text-sm" />
                  <span>Return to Gallery</span>
                </button>
                <div className="flex items-center gap-4">
                  <a
                    href={activeProject.githubUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-xs font-black uppercase tracking-widest text-gray-400 hover:text-white transition-colors"
                  >
                    <i className="fi fi-brands-github text-sm" />
                    <span>Codebase</span>
                  </a>
                  <a
                    href={activeProject.liveUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-gradient px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest text-white shadow-xl shadow-indigo-500/10"
                  >
                    Live Demo
                  </a>
                </div>
              </div>

              {/* Project Title Block */}
              <div className="mb-8 relative z-10">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="text-3xl sm:text-4xl font-black text-white font-[var(--font-poppins)] tracking-tight">
                    {activeProject.title}
                  </h3>
                  <span className={`text-[8px] px-2 py-0.5 rounded-full font-black uppercase tracking-widest border border-white/10 ${activeProject.statusColor}`}>
                    {activeProject.status}
                  </span>
                </div>
                <span className="text-xs font-bold text-indigo-400 uppercase tracking-widest block font-mono">
                  {activeProject.subtitle} • {activeProject.year} Production deployment
                </span>
              </div>

              {/* Detailed specs */}
              <div className="space-y-6 relative z-10 text-sm leading-relaxed text-gray-300 font-medium">
                <div>
                  <h4 className="text-xs font-black uppercase text-indigo-300 tracking-wider mb-1.5">Problem Statement</h4>
                  <p>{activeProject.problem}</p>
                </div>

                <div>
                  <h4 className="text-xs font-black uppercase text-indigo-300 tracking-wider mb-1.5">R&D and Research</h4>
                  <p>{activeProject.research}</p>
                </div>

                <div className="grid md:grid-cols-2 gap-6 bg-white/[0.02] border border-white/5 p-5 rounded-2xl">
                  <div>
                    <h5 className="text-[10px] font-black uppercase tracking-widest text-indigo-400/80 mb-1">Architecture Node</h5>
                    <p className="text-xs text-white">{activeProject.architecture}</p>
                  </div>
                  <div>
                    <h5 className="text-[10px] font-black uppercase tracking-widest text-indigo-400/80 mb-1">Performance Audits</h5>
                    <p className="text-xs text-emerald-400 font-bold">{activeProject.performance}</p>
                  </div>
                </div>

                <div>
                  <h4 className="text-xs font-black uppercase text-indigo-300 tracking-wider mb-1.5">Security & Authentication</h4>
                  <p>{activeProject.authentication}</p>
                </div>

                <div>
                  <h4 className="text-xs font-black uppercase text-indigo-300 tracking-wider mb-1.5">Database Relational Schema</h4>
                  <pre className="p-4 rounded-xl bg-black/60 border border-white/5 overflow-x-auto text-[11px] font-mono text-indigo-300 leading-normal">
                    {activeProject.databaseSchema}
                  </pre>
                </div>

                <div>
                  <h4 className="text-xs font-black uppercase text-indigo-300 tracking-wider mb-2">Primary API Structures</h4>
                  <ul className="space-y-2">
                    {activeProject.apiStructure.map((api, idx) => (
                      <li key={idx} className="flex gap-3 bg-white/[0.02] border border-white/5 p-3 rounded-xl text-xs font-mono">
                        <span className="text-emerald-400 font-bold shrink-0">GET/POST</span>
                        <span className="text-white">{api}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h4 className="text-xs font-black uppercase text-indigo-300 tracking-wider mb-1.5">Code Snippet Preview</h4>
                  <pre className="p-4 rounded-xl bg-black/60 border border-white/5 overflow-x-auto text-[11px] font-mono text-gray-300 leading-normal">
                    <code>{activeProject.codeSnippet}</code>
                  </pre>
                </div>

                <div>
                  <h4 className="text-xs font-black uppercase text-indigo-300 tracking-wider mb-2">Deploying Infrastructure</h4>
                  <p className="text-xs mb-3">{activeProject.deployment}</p>
                </div>

                <div>
                  <h4 className="text-xs font-black uppercase text-indigo-300 tracking-wider mb-2">Project Roadmap</h4>
                  <ul className="space-y-1.5 text-xs text-gray-400">
                    {activeProject.roadmap.map((item, idx) => (
                      <li key={idx} className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-indigo-500" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </section>
  );
}
